import React, { useEffect, useMemo } from 'react';
import { Button, Card, Checkbox, Col, Form, Input, notification, Row, Select, Space, Typography } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createMiniAppRequest } from '../../../../services/onlinestore/miniAppsServiceRequest';
import { AppSelect } from '../../../../constants/appSelect';
import { CloseOutlined, ContainerOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getListPartnersRequest } from '../../../../services/onlinestore/partnerServiceRequest';
import UploadButton from '../../../../components/UploadButton';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const { Text, Title } = Typography;

const CreateMiniApp = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchHaveTerm = Form.useWatch('have_term', form);
  const watchHideAppBar = Form.useWatch('hide_appbar', form);

  useEffect(() => {
    if (!watchHaveTerm) {
      form.setFieldValue('term_image', null);
      form.setFieldValue('term_title', null);
      form.setFieldValue('term_description', null);
      form.setFieldValue('term_content', null);
    }
  }, [watchHaveTerm, form]);

  // get data partner
  const { data: dataPartners } = useQuery({
    queryKey: ['dataPartners'],
    queryFn: () => getListPartnersRequest({ page: 1, limit: 1000, status: [1] }),
  });

  // convert list partners options
  const dataPartnersOption = useMemo(() => {
    return dataPartners?.data
      ?.filter((item) => item?.status === 1)
      ?.map((item) => {
        return { label: item?.title, value: item?.id };
      });
  }, [dataPartners]);

  // handle submit form
  const { mutate: mutateCreateMiniApp, isPending } = useMutation({
    mutationFn: createMiniAppRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Thêm mới miniapp thành công!',
        });
        navigate(-1);
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (data) => {
    mutateCreateMiniApp({
      ...data,
      hide_appbar: data?.hide_appbar ? 1 : 0,
      have_term: data?.have_term ? 1 : 0,
      access_property: data?.access_property ? data?.access_property : [],
    });
  };

  return (
    <Form
      name="create-miniapp"
      form={form}
      layout="vertical"
      className="mb-5"
      onFinish={handleSubmitForm}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm miniapp</Title>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="icon"
            label={
              <Text>
                Upload icon <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng upload icon' }]}
          >
            <UploadButton name="icon-mini-app" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="title"
            label={
              <Text>
                Tên miniapp <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập tên miniapp' }]}
          >
            <Input placeholder="Nhập tên miniapp..." />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="description"
            label={
              <Text>
                Mô tả <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
          >
            <Input placeholder="Nhập mô tả..." />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="partner_id"
            label={
              <Text>
                Đối tác <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn đối tác' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn đối tác"
              options={dataPartnersOption}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item name="hide_appbar" valuePropName="checked" label=" ">
            <Checkbox>
              <Text>Ẩn thanh appbar khi mở webview</Text>
            </Checkbox>
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="open_url"
            label={
              <Text>
                Đường link khởi chạy (urlOpen) <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập đường link khởi chạy' }]}
          >
            <Input placeholder="Nhập đường link khởi chạy" />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="close_url"
            label={
              <Text>
                Đường link kết thúc (urlClose) {watchHideAppBar ? <span className="text-red-700">*</span> : ''}
              </Text>
            }
            rules={[{ required: watchHideAppBar, message: 'Vui lòng nhập đường link kết thúc' }]}
          >
            <Input placeholder="Nhập đường link kết thúc" />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item name="color" label={<Text>Mã màu</Text>}>
            <Input placeholder="Nhập mã màu" />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="public_key"
            label={
              <Text>
                Public key <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập public key' }]}
          >
            <Input placeholder="Nhập public key" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item name="have_term" valuePropName="checked">
            <Checkbox>
              <Text>Điều khoản sử dụng</Text>
            </Checkbox>
          </Form.Item>
        </Col>
        {watchHaveTerm ? (
          <Col xs={24}>
            <Card>
              <Col xs={24}>
                <Form.Item
                  name="term_image"
                  label={
                    <Text>
                      Upload ảnh <span className="text-red-700">*</span>
                    </Text>
                  }
                  rules={[{ required: true, message: 'Vui lòng upload ảnh' }]}
                >
                  <UploadButton name="term_image" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="term_title"
                  label={
                    <Text>
                      Tiêu đề <span className="text-red-700">*</span>
                    </Text>
                  }
                  rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                >
                  <Input placeholder="Nhập tiêu đề" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name="term_description"
                  label={
                    <Text>
                      Mô tả <span className="text-red-700">*</span>
                    </Text>
                  }
                  rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                >
                  <Input placeholder="Nhập mô tả" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name="term_content"
                  label={<Text>Nội dung</Text>}
                  valuePropName="value" // Quan trọng: Mapping giá trị với React-Quill
                  getValueFromEvent={(e) => e} // Đảm bảo giá trị trả về đúng dạng
                >
                  <ReactQuill
                    theme="snow"
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, false] }],
                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                        ['link', 'image'],
                        ['clean'],
                      ],
                    }}
                    formats={[
                      'header',
                      'bold',
                      'italic',
                      'underline',
                      'strike',
                      'blockquote',
                      'list',
                      'bullet',
                      'indent',
                      'link',
                      'image',
                    ]}
                    className="h-[248px] mb-2"
                  />
                </Form.Item>
              </Col>
            </Card>
          </Col>
        ) : (
          ''
        )}
        <Col xs={12}>
          <Form.Item name="access_property" label={<Text>Thông tin chia sẻ cho miniapp</Text>}>
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              mode="multiple"
              placeholder="Chọn thông tin chia sẻ"
              options={AppSelect.MINI_APP_INFO_SHARE}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="status"
            label={
              <Text>
                Trạng thái <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateMiniApp;
